import _, {uniq} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Edge} from 'reactflow';

import {
  useLazyUnmountCancelToken,
  usePrevious,
} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {
  ChannelPreferenceFrontChatAiKnowledgeSourceModel,
  FrontChatAiKnowledgeSourceTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/channelPreferencesModel';
import {CompanyFeaturesEnum} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {
  ApiDecisionTree,
  DecisionTreeModel,
  DecisionTreeTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/decisionTreeModel';
import {
  DecisionTreeNodeDataSemanticSearchFrontChat,
  isSemanticSearchNodeData,
} from '../../../../../libs/shared/core/src/models/api/decisionTreeNodeDataModel';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  KnowledgeBaseModel,
  KnowledgeBaseStatusesEnum,
  SEMANTIC_SEARCH_SUPPORTED_LOCALES,
} from '../../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../../../../libs/shared/core/src/services/http/httpError';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {fetchChannelByIdAsyncAction} from '../../../../../libs/shared/core/src/store/data/channels/commonChannelsActions';
import {getFlatPublishedExternalKnowledgeBases} from '../../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {
  getHasChatbotDynamicVariables,
  getHasChatbotParseTagArchive,
  getHasChatbotReusableSubflows,
} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {gatherNodeDescendantIds} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {DecisionTreeNode} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {CreateDecisionTree} from '../../../../settings/chatbots/settingsChatBotsHelpers';
import {CustomFields} from '../../../../settings/editors/commons/customFieldsModel';
import {useFetchSubtreesForDecisionTreeId} from '../../../../settings/fetchers/decisionTrees';
import {useTags} from '../../../../settings/rules/ruleEditor/parameters/tagParameter';
import {getHasAiAnswersStep} from '../../../../store/aiAnswers/aiAnswerSelectors';
import {
  getCompany,
  getCompanyUrl,
  getFindHasCompanyFeature,
  getHasTrialFeatureAiAnswers,
} from '../../../../store/data/company/companySelectors';
import {fetchInboxByIdAsyncAction} from '../../../../store/data/inboxes/inboxesActions';
import {defaultChatBotBuilderSubtreeState} from '../defaultTree';
import {gatherNodeTagIdsIncludeSubtrees} from '../helpers';
import {ChatBotBuilderFeatureFlags, ChatBotBuilderNode} from '../types';

/*
 * Constants.
 */

export enum ChatBotBuilderDowngradeReasonsEnum {
  IS_MISSING_PREMIUM_FEATURE = 'is_missing_premium_feature',
  IS_MISSING_AI_ANSWERS = 'is_missing_ai_answers',
}

/*
 * Types.
 */

export interface ChatBotBuilderAppProps {
  featureFlags: ChatBotBuilderFeatureFlags;
  isUserGlobalAdmin: boolean;
  hasQualBotsFeature: boolean;
  hasPremiumFeatures: boolean;
  isOnLegacyGrowthScalePlan: boolean;
  shouldRenderTrialBanner: boolean | undefined;
  companyCreatedAt: number;
  customFields: CustomFields;
  downgradeReason?: ChatBotBuilderDowngradeReasonsEnum;
  channel?: ChannelModel;
  inbox?: InboxModel;
  idTagMap: Record<number, TagModel>;
  isKnowledgeBaseEnabledForAi: boolean;
  aiKnowledgeSources: ReadonlyArray<ChannelPreferenceFrontChatAiKnowledgeSourceModel>;
  knowledgeBase: KnowledgeBaseModel | undefined;
  subtrees: ReadonlyArray<DecisionTreeModel>;
  disabledNodeIds: ReadonlyArray<string>;
  onSubtreeCreate: (name: string) => Promise<DecisionTreeModel | undefined>;
  isSimpleEditor?: boolean;
}

/*
 * Hooks.
 */

/**
 * ChatBotBuilder and its children should not directly select or dispatch to the redux store. This hook can be
 * used by ChatBotBuilder's parent component to retrieve values from the store which are needed for nodes such
 * as the Custom Field case. Note that this will also fetch the channel and inbox that is associated with the
 * provided channelId.
 */
export function useFetchChatBotBuilderAppProps(
  namespace: string,
  channelId: number | undefined,
  customFields: CustomFields,
  nodes: ReadonlyArray<ChatBotBuilderNode>,
  edges: ReadonlyArray<Edge<any>>,
  decisionTreeId?: number,
  hasPremiumFeatures?: boolean,
  downgradeReason?: ChatBotBuilderDowngradeReasonsEnum,
  isSimpleEditor?: boolean,
): ChatBotBuilderAppProps {
  // Feature flags.
  const featureFlags = useChatBotBuilderFeatureFlags();

  const [channel, setChannel] = useState<ChannelModel | undefined>();
  const [inbox, setInbox] = useState<InboxModel | undefined>();

  const shouldRenderTrialBanner = useAppSelector(getHasTrialFeatureAiAnswers);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const company = useAppSelector(getCompany);
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const hasQualBots = findHasCompanyFeature(CompanyFeaturesEnum.CHAT_BOTS);

  const companyUrl = useAppSelector(getCompanyUrl);
  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  const prevNamespace = usePrevious(channel?.namespace);
  const prevDecisionTreeId = usePrevious(decisionTreeId);

  const {subtrees, refetch: refetchSubtrees} = useFetchSubtreesForDecisionTreeId(namespace, decisionTreeId);

  useEffect(() => {
    if (channel?.namespace !== prevNamespace || decisionTreeId !== prevDecisionTreeId) {
      refetchSubtrees();
    }
  }, [channel?.namespace, decisionTreeId, prevDecisionTreeId, prevNamespace, refetchSubtrees]);

  const {tagIds, maybeTagIds} = useMemo(
    () => gatherNodeTagIdsIncludeSubtrees(nodes, subtrees),
    [nodes, subtrees],
  );
  const {allVisibleTags} = useTags(uniq([...tagIds, ...maybeTagIds]), '');
  const idTagMap: Record<number, TagModel> = useMemo(
    () => allVisibleTags.reduce((currentTagIdMap, tag) => ({...currentTagIdMap, [tag.id]: tag}), {}),
    [allVisibleTags],
  );

  // The bot builder does not manage the knowledge base, but it is provided context on the channel's knowledge base.
  const knowledgeBases: ReadonlyArray<KnowledgeBaseModel> = useAppSelector(
    getFlatPublishedExternalKnowledgeBases,
  );
  const [isKnowledgeBaseEnabledForAi, channelKnowledgeBase, aiKnowledgeSources] = useMemo(() => {
    // Do not attempt to find bot builder's KB unless this is a chat channel that has enabled the KB preference.
    if (!channel || channel.type !== ChannelTypesEnum.FRONT_CHAT) {
      return [false, undefined];
    }

    const {aiKnowledgeSources: knowledgeSources} = channel.preferences;
    return [
      channel.preferences.isKnowledgeBaseEnabledForAi,
      knowledgeBases.find((knowledgeBase) => {
        // If the source is a KB and the current KB we are looking at is the correct one.
        if (
          knowledgeSources[0]?.type === FrontChatAiKnowledgeSourceTypesEnum.KNOWLEDGE_BASE &&
          knowledgeBase.id === knowledgeSources[0].id
        ) {
          // Make sure the KB is a valid source.
          return _(SEMANTIC_SEARCH_SUPPORTED_LOCALES).some(
            (locale) => knowledgeBase.statuses[locale] === KnowledgeBaseStatusesEnum.PUBLISHED,
          );
        }
        return false;
      }),
      knowledgeSources,
    ];
  }, [channel, knowledgeBases]);

  const dispatch = useAppDispatch();

  // Computes all disabled nodes based on if they are in a path that contains a semantic search node.
  const disabledNodeIds = useMemo(() => {
    if (downgradeReason !== ChatBotBuilderDowngradeReasonsEnum.IS_MISSING_AI_ANSWERS) {
      return [];
    }

    // Find all the semantic search nodes.
    const invalidNodeIds = new Set<string>([]);
    const semanticSearchNodes: ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataSemanticSearchFrontChat>> =
      _(nodes)
        .filter((node) => isSemanticSearchNodeData(node.data))
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        .map((node) => node as DecisionTreeNode<DecisionTreeNodeDataSemanticSearchFrontChat>)
        .compact()
        .value();

    // If there is no semantic search node, then there is no invalid node.
    if (semanticSearchNodes.length === 0) {
      return [];
    }

    for (const semanticSearchNode of semanticSearchNodes) {
      // Get all the handle for the "No" path as all other paths are ignored.
      const ignoredNotHandledSourceIds = _(semanticSearchNode.data.options)
        .filter((option) => option.value === 'No')
        .map((option) => option.sourceHandle)
        .value();
      const data = gatherNodeDescendantIds(
        semanticSearchNode.id,
        [...nodes],
        [...edges],
        ignoredNotHandledSourceIds,
      );
      // We do not want to add the node itself to the invalid list as it is not fully
      // invalid unless it is already in a chain of invalid nodes.
      data.nodeIds.forEach((nodeId) =>
        nodeId !== semanticSearchNode.id ? invalidNodeIds.add(nodeId) : undefined,
      );
    }

    return Array.from(invalidNodeIds);
  }, [downgradeReason, nodes, edges]);

  useEffect(() => {
    const fetchDecisionTreeChannel = async () => {
      const decisionTreeChannel = channelId
        ? await dispatch(fetchChannelByIdAsyncAction(channelId))
        : undefined;

      if (decisionTreeChannel && decisionTreeChannel.type === ChannelTypesEnum.FRONT_CHAT) {
        setChannel(decisionTreeChannel);

        const decisionTreeInbox = await dispatch(fetchInboxByIdAsyncAction(decisionTreeChannel?.inboxId));

        if (decisionTreeInbox) {
          setInbox(decisionTreeInbox);
        }
      }
    };

    fetchDecisionTreeChannel();
  }, [channelId, channel, inbox, dispatch]);

  const onSubtreeCreate = useCallback(
    async (subtreeName: string) => {
      if (!decisionTreeId || !channel) {
        throw new Error('Failed to create subtree with missing parentDecisionTree or channel');
      }

      // TODO: Show error here if there will be a name collision.

      const defaultSubtreeData = {
        nodes: defaultChatBotBuilderSubtreeState.nodes,
        edges: defaultChatBotBuilderSubtreeState.edges,
      };

      const subtreeCreateData: CreateDecisionTree = {
        name: subtreeName,
        channel_ids: [],
        settings: {},
        namespace: channel.namespace,
        channel_type: channel.type,
        company_id: company.id,
        tree_type: DecisionTreeTypesEnum.SUBTREE,
        parent_decision_tree_id: decisionTreeId,
        tree_data: defaultSubtreeData,
        subtree_ids: [],
        expressions: [],
      };

      try {
        const newSubtree = await postApiAsync(
          ApiDecisionTree,
          `${companyUrl}/namespaces/${channel.namespace}/decision_trees`,
          subtreeCreateData,
          {
            cancelToken: buildUnmountCancelToken(),
          },
        );

        // TODO: Interface event for subtree create click

        // Fetch the subtrees again now that we've created a new one for the list.
        refetchSubtrees();
        return newSubtree;
      } catch (error) {
        if (!(error instanceof HttpError)) {
          reportToBugsnag(error);
        }
        throw error;
      }
    },
    [buildUnmountCancelToken, channel, company.id, companyUrl, decisionTreeId, refetchSubtrees],
  );

  return {
    featureFlags,
    isUserGlobalAdmin,
    companyCreatedAt: company.createdAt,
    customFields,
    downgradeReason,
    hasPremiumFeatures: Boolean(hasPremiumFeatures),
    hasQualBotsFeature: hasQualBots,
    // If the company doesn't have access to premium features but does have access to Qualification Bots
    isOnLegacyGrowthScalePlan: Boolean(!hasPremiumFeatures && hasQualBots),
    shouldRenderTrialBanner: !isSimpleEditor && shouldRenderTrialBanner,
    channel,
    inbox,
    idTagMap,
    isKnowledgeBaseEnabledForAi,
    aiKnowledgeSources: aiKnowledgeSources ?? [],
    knowledgeBase: channelKnowledgeBase,
    subtrees: subtrees ?? [],
    disabledNodeIds,
    onSubtreeCreate,
    isSimpleEditor,
  };
}

function useChatBotBuilderFeatureFlags(): ChatBotBuilderFeatureFlags {
  const hasChatbotArchiveAction = useAppSelector(getHasChatbotParseTagArchive);
  const hasChatbotsTagNode = useAppSelector(getHasChatbotParseTagArchive);
  const hasChatbotsParseTextNode = useAppSelector(getHasChatbotParseTagArchive);
  const hasAiAnswersStep = useAppSelector(getHasAiAnswersStep);
  const hasSubtreeNode = useAppSelector(getHasChatbotReusableSubflows);
  const hasChatbotDynamicVariables = useAppSelector(getHasChatbotDynamicVariables);

  return useMemo(
    () => ({
      hasChatbotArchiveAction,
      hasChatbotsTagNode,
      hasChatbotsParseTextNode,
      hasAiAnswersStep,
      hasSubtreeNode,
      hasChatbotDynamicVariables,
    }),
    [
      hasChatbotArchiveAction,
      hasChatbotDynamicVariables,
      hasChatbotsParseTextNode,
      hasChatbotsTagNode,
      hasAiAnswersStep,
      hasSubtreeNode,
    ],
  );
}
